import { nanoid } from 'nanoid'
import { AppDispatch } from '@/store/store'
import {
  BatchImage,
  BatchImageDataPatch,
  resolveBatchImagesData,
  setBatchImages,
  setItemStatus,
} from '@/store/slices/batch/slice'
import { readImageFileSizeFromHeader } from '@/utils/imageHeaderSize'
import { BATCH_STUDIO_ROUTE } from '@/scenes/Dashboard/BatchEdit/batchRoutes'
import { loadBatchImageFileData } from './batchEditService'
import { defaultPosition } from './positionFit'

/**
 * Deferred file import: awaiting a whole selection (up to the plan's batch image limit)
 * before navigating left the entry gate frozen. Instead the grid opens on one
 * placeholder card per picked file, filled in over two passes — intrinsic size from
 * the file header first, so cards take their real shape without waiting on a decode,
 * then the bytes, which is the slow part and what background removal needs.
 */

/** Header reads are small slice reads, not decodes, so this pass can go wide. */
const SIZE_PROBE_CONCURRENCY = 32

/** Decoding is main-thread work: a wider pool doesn't finish sooner. */
const IMPORT_CONCURRENCY = 6

/** Patches dispatch in bursts on this cadence. With hundreds of cards mounted,
 *  one action per file would re-render the grid hundreds of times (React 17 does
 *  not batch updates from promise callbacks). */
const FLUSH_INTERVAL_MS = 100

export interface PendingBatchImport {
  id: string
  file: File
}

export interface BatchImportPlaceholders {
  images: BatchImage[]
  pending: PendingBatchImport[]
}

/**
 * Starts a new batch from a picked selection: placeholder cards, then the studio, then
 * the files resolve behind it. Returns how many cards were added, so a caller can word
 * its own "too many photos" message; the reads continue after it returns.
 */
export function beginBatchImport(
  files: File[],
  dispatch: AppDispatch,
  history: { push: (path: string) => void },
): number {
  const { images, pending } = createBatchImagePlaceholders(files)
  if (!images.length) return 0
  dispatch(setBatchImages(images))
  history.push(BATCH_STUDIO_ROUTE)
  void loadPendingBatchImports(pending, dispatch)
  return images.length
}

/** `pending` pairs each card's id with its File, for `loadPendingBatchImports`. */
export function createBatchImagePlaceholders(files: File[]): BatchImportPlaceholders {
  const pending: PendingBatchImport[] = files.map((file) => ({ id: nanoid(), file }))
  const images: BatchImage[] = pending.map(({ id, file }) => ({
    id,
    name: file.name,
    // Both arrive via resolveBatchImagesData; until then the card has no intrinsic
    // ratio and renders square.
    originalDataUrl: '',
    originalAspectRatio: undefined,
    bgRemoved: false,
    bgColor: '#FFFFFF',
    cutoutPosition: defaultPosition(),
    // 'processing' from the start, so the card shows its loader while reading and
    // hands over to auto bg-removal without an un-loadered frame between.
    status: 'processing',
    error: null,
  }))
  return { images, pending }
}

/**
 * Fills in the placeholder cards. Fire-and-forget: patches for cards the store no
 * longer knows about are dropped by the reducer, so leaving the studio needs no
 * explicit cancellation.
 */
export async function loadPendingBatchImports(
  pending: PendingBatchImport[],
  dispatch: AppDispatch,
): Promise<void> {
  await runResolvePool(pending, SIZE_PROBE_CONCURRENCY, dispatch, async (item) => {
    const size = await readImageFileSizeFromHeader(item.file)
    if (!size) return null
    return {
      id: item.id,
      originalWidth: size.width,
      originalHeight: size.height,
      originalAspectRatio: size.width / size.height,
    }
  })

  // Re-reports size: the decoded value wins if the header parse disagreed.
  await runResolvePool(
    pending,
    IMPORT_CONCURRENCY,
    dispatch,
    async (item) => ({ id: item.id, ...(await loadBatchImageFileData(item.file)) }),
    // `error: null` lets the card render its own localized fallback text; the reason
    // itself only helps a developer, so it goes to the console.
    (item, error) => {
      console.error(`batch import: ${item.file.name} failed to load`, error)
      dispatch(setItemStatus({ id: item.id, status: 'error', error: null }))
    },
  )
}

/** Runs `resolve` over every item with bounded concurrency, dispatching the patches
 *  it returns in bursts. */
async function runResolvePool(
  pending: PendingBatchImport[],
  concurrency: number,
  dispatch: AppDispatch,
  resolve: (item: PendingBatchImport) => Promise<BatchImageDataPatch | null>,
  onError?: (item: PendingBatchImport, error: unknown) => void,
): Promise<void> {
  const queue = [...pending]
  let buffer: BatchImageDataPatch[] = []
  let flushTimer: ReturnType<typeof setTimeout> | null = null

  const flush = () => {
    if (flushTimer !== null) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    if (!buffer.length) return
    dispatch(resolveBatchImagesData(buffer))
    buffer = []
  }

  const worker = async () => {
    while (queue.length) {
      const item = queue.shift()
      if (!item) return
      try {
        const patch = await resolve(item)
        if (!patch) continue
        buffer.push(patch)
        if (flushTimer === null) flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS)
      } catch (error) {
        onError?.(item, error)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker))
  flush()
}
